// ------------------------------------------------------------------------ //
// TanStack Query hooks for Activities
// ------------------------------------------------------------------------ //
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@shared/api/api';
import dayjs from 'dayjs';
import { getUserTimezone } from '@shared/utils/timezone';

// ------------------------------------------------------------------------ //
// Query Keys
// ------------------------------------------------------------------------ //
export const activityKeys = {
  all: ['activities'],
  user: (userId, startedAt, endedAt) => [...activityKeys.all, 'user', userId, startedAt, endedAt],
  inProgress: () => [...activityKeys.all, 'inProgress'],
};

// ------------------------------------------------------------------------ //
// Update Activity Mutation
// ------------------------------------------------------------------------ //
export function useUpdateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      return await api.patchActivity(data);
    },
    onSuccess: () => {
      // Invalidate all activity queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: activityKeys.all });
    },
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (handled by refresh token logic)
      if (error?.response?.status === 401) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
}

// ------------------------------------------------------------------------ //
// Create Activity Mutation
// ------------------------------------------------------------------------ //
export function useCreateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      return await api.postActivity(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityKeys.all });
    },
  });
}

// ------------------------------------------------------------------------ //
// Delete Activity Mutation
// ------------------------------------------------------------------------ //
export function useDeleteActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      return await api.deleteActivity(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityKeys.all });
    },
  });
}

// ------------------------------------------------------------------------ //
// Get Activities User Query
// ------------------------------------------------------------------------ //
export function useActivitiesUser({ userId, date, activityTypes }) {
  const userTimezone = getUserTimezone();

  return useQuery({
    queryKey: activityKeys.user(
      userId,
      dayjs.tz(date, userTimezone).hour(0).minute(0).second(0).date(1).unix(),
      dayjs.tz(date, userTimezone).hour(23).minute(59).second(59).date(31).unix()
    ),
    queryFn: async () => {
      const response = await api.getActivitiesUser({
        userId: userId,
        startedAt: dayjs.tz(date, userTimezone).hour(0).minute(0).second(0).date(1).unix(),
        endedAt: dayjs.tz(date, userTimezone).hour(23).minute(59).second(59).date(31).unix(),
      });

      if (!response) {
        throw new Error('Failed to fetch activities');
      }

      // Transform the data
      const monthActivities = response.map((activity) => {
        const end = dayjs(activity.ended_at);
        const start = dayjs(activity.started_at);

        if (activity.ended_at === null) {
          activity.duration = 'en cours';
        } else {
          const duration = end.diff(start, 'minute');
          const hours = Math.floor(duration / 60);
          const minutes = duration % 60;

          if (hours === 0) {
            activity.duration = minutes + 'min';
          } else if (minutes === 0) {
            activity.duration = hours + 'h';
          } else {
            activity.duration = hours + 'h' + minutes;
          }
        }

        if (activity.activity_type_id === 4) {
          activity.activity_type = activity.special_activity_name;
        } else {
          const actionType = activityTypes?.find((x) => x.id === activity.activity_type_id);
          if (actionType) {
            activity.activity_type = actionType.name;
          }
        }

        return activity;
      });

      return monthActivities;
    },
    enabled: !!userId && !!date && !!activityTypes,
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (handled by refresh token logic)
      if (error?.response?.status === 401) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
}

// ------------------------------------------------------------------------ //
// Get All Users Activities Query (for global journal)
// ------------------------------------------------------------------------ //
export function useAllUsersActivities({ date, activityTypes }) {
  const userTimezone = getUserTimezone();

  return useQuery({
    queryKey: [...activityKeys.all, 'allUsers',
      dayjs.tz(date, userTimezone).hour(0).minute(0).second(0).date(1).unix(),
      dayjs.tz(date, userTimezone).hour(23).minute(59).second(59).date(31).unix()
    ],
    queryFn: async () => {
      const response = await api.getActivities({
        startedAt: dayjs.tz(date, userTimezone).hour(0).minute(0).second(0).date(1).unix(),
        endedAt: dayjs.tz(date, userTimezone).hour(23).minute(59).second(59).date(31).unix(),
      });

      if (!response) {
        throw new Error('Failed to fetch activities');
      }

      // Transform the data
      const monthActivities = response.map((activity) => {
        const end = dayjs(activity.ended_at);
        const start = dayjs(activity.started_at);

        if (activity.ended_at === null) {
          activity.duration = 'en cours';
        } else {
          const duration = end.diff(start, 'minute');
          const hours = Math.floor(duration / 60);
          const minutes = duration % 60;

          if (hours === 0) {
            activity.duration = minutes + 'min';
          } else if (minutes === 0) {
            activity.duration = hours + 'h';
          } else {
            activity.duration = hours + 'h' + minutes;
          }
        }

        if (activity.activity_type_id === 4) {
          activity.activity_type = activity.special_activity_name;
        } else {
          const actionType = activityTypes?.find((x) => x.id === activity.activity_type_id);
          if (actionType) {
            activity.activity_type = actionType.name;
          }
        }

        // Add user information from the nested user object
        if (activity.user) {
          // Check if it's a teacher
          if (activity.user.teacher) {
            activity.user_name = `${activity.user.teacher.first_name} ${activity.user.teacher.last_name}`;
          }
          // Check if it's a switchman
          else if (activity.user.switchman) {
            activity.user_name = `${activity.user.switchman.first_name} ${activity.user.switchman.last_name}`;
          }
          // Fallback if neither teacher nor switchman
          else {
            activity.user_name = `User ${activity.user_id}`;
          }
        } else {
          activity.user_name = activity.user_id ? `User ${activity.user_id}` : 'Unknown';
        }

        return activity;
      });

      return monthActivities;
    },
    enabled: !!date && !!activityTypes,
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (handled by refresh token logic)
      if (error?.response?.status === 401) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
}
